import { useRef, useEffect, useState } from "react";
import { useProductGallery } from "../hooks/useProductGallery";
import playIcon from "../../../../assets/icons/play-icon.webp";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ShareButton from "./ShareButton";
import { ChevronUp, ChevronDown } from "lucide-react";

function ProductGallery({ data, selectedColor }) {
  const thumbRef = useRef(null);

  const {
    loading,
    localLoading,
    selectedImage,
    zoomStyle,
    imageRef,
    setSelectedImage,
    handleMouseMove,
    handleMouseLeave,
  } = useProductGallery({ data, selectedColor });

  /* MOBILE DETECT */
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  /* TOUCH ZOOM */
  const [touchZoomStyle, setTouchZoomStyle] = useState({});

  /* SWIPE */
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  /* ZOOM CONTROL */
  const isZooming = useRef(false);
  const initialDistance = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* MEDIA */
  const mediaList = selectedColor || [];

  /* =========================
     TOUCH + SWIPE FIXED LOGIC
  ========================= */

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      //  SWIPE START
      isZooming.current = false;
      touchStartX.current = e.touches[0].clientX;
    }

    if (e.touches.length === 2) {
      //  PINCH START
      isZooming.current = true;

      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;

      initialDistance.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = (e) => {
    if (!imageRef.current) return;

    //  PINCH ZOOM
    if (e.touches.length === 2 && isZooming.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;

      const newDistance = Math.sqrt(dx * dx + dy * dy);
      let scale = newDistance / initialDistance.current;

      scale = Math.max(1, Math.min(scale, 3)); // limit zoom

      setTouchZoomStyle({
        transform: `scale(${scale})`,
        transformOrigin: "center",
      });

      return;
    }

    //  SWIPE MOVE
    if (!isZooming.current && e.touches.length === 1) {
      touchEndX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = () => {
    //  SWIPE ONLY
    if (!isZooming.current) {
      const diff = touchStartX.current - touchEndX.current;

      if (Math.abs(diff) > 50 && mediaList.length > 1) {
        const currentIndex = mediaList.findIndex(
          (item) => item.media === selectedImage
        );

        if (diff > 0) {
          // NEXT
          const nextIndex = (currentIndex + 1) % mediaList.length;
          setSelectedImage(mediaList[nextIndex].media);
        } else {
          // PREVIOUS
          const prevIndex =
            (currentIndex - 1 + mediaList.length) % mediaList.length;
          setSelectedImage(mediaList[prevIndex].media);
        }
      }
    }

    //  RESET ZOOM
    setTouchZoomStyle({
      transform: "scale(1)",
      transformOrigin: "center",
    });

    isZooming.current = false;
  };

  /* THUMB COUNT */
  const thumbnailCount =
    mediaList.length + (data?.video_source ? 1 : 0);

  const showScrollButtons = isMobile
    ? thumbnailCount > 3
    : thumbnailCount > 7;

  /* DESKTOP SCROLL */
  const scrollUp = () => {
    thumbRef.current?.scrollBy({ top: -120, behavior: "smooth" });
  };

  const scrollDown = () => {
    thumbRef.current?.scrollBy({ top: 120, behavior: "smooth" });
  };

  /* RESET IMAGE */
  useEffect(() => {
    if (mediaList.length) {
      setSelectedImage(mediaList[0].media);
    }
  }, [selectedColor, setSelectedImage]);

  return (
    <div className="product-gallery gallery-section-page">
      
      {/* THUMBNAILS */}
      <div className="thumbnail-column-wrapper">

        {showScrollButtons && !isMobile && (
          <>
            <button className="thumb-scroll-btn up" onClick={scrollUp}>
              <ChevronUp size={20} />
            </button>

            <button
              className="thumb-scroll-btn down"
              onClick={scrollDown}
              style={{ bottom: "7px" }}
            >
              <ChevronDown size={20} />
            </button>
          </>
        )}

        <div className="thumbnail-column" ref={thumbRef}>
          {localLoading ? (
            Array(4)
              .fill(0)
              .map((_, i) => (
                <Skeleton
                  key={i}
                  height={70}
                  width={70}
                  style={{ marginBottom: 10 }}
                />
              ))
          ) : (
            <>
              {mediaList.map((img, index) => (
                <img
                  key={index}
                  src={img.media}
                  alt={`Thumb ${index}`}
                  className={`thumbnail ${
                    selectedImage === img.media ? "selected-thumb" : ""
                  }`}
                  onClick={() => setSelectedImage(img.media)}
                />
              ))}

              {data?.video_source && (
                <div
                  className={`thumbnail video-thumb ${
                    selectedImage === "video" ? "selected-thumb" : ""
                  }`}
                  onClick={() => setSelectedImage("video")}
                >
                  <img src={playIcon} alt="Video" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* MAIN IMAGE */}
      <div className="main-image-column">
        <div className="mobile-displaynone share-btn">
          <ShareButton />
        </div>

        {loading || localLoading || !selectedImage ? (
          <div className="main-image-skeleton">
            <Skeleton width="100%" height="100%" />
          </div>
        ) : selectedImage === "video" ? (
          data?.video_source?.includes("youtube.com") ? (
            <iframe
              width="100%"
              height="100%"
              src={`${data?.video_source}?autoplay=1&mute=1&playsinline=1`}
              title="Product Video"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <video
              autoPlay
              muted
              controls
              className="main-image"
              style={{ objectFit: "cover", width: "100%", height: "100%" }}
            >
              <source src={data?.video_source} type="video/mp4" />
            </video>
          )
        ) : (
          <div
            className="zoom-container"
            onMouseMove={!isMobile ? handleMouseMove : undefined}
            onMouseLeave={!isMobile ? handleMouseLeave : undefined}
            onTouchStart={isMobile ? handleTouchStart : undefined}
            onTouchMove={isMobile ? handleTouchMove : undefined}
            onTouchEnd={isMobile ? handleTouchEnd : undefined}
          >
            <img
              ref={imageRef}
              src={selectedImage}
              alt="Main Product"
              className="main-image zoom-image"
              style={isMobile ? touchZoomStyle : zoomStyle}
            />
          </div>
        )}
      </div>
    </div>
  );
}
export default ProductGallery;